import { useEffect, useId } from "react";
import {
  getLayerRoot,
  registerLayer,
  topModalLayerId,
  unregisterLayer,
} from "./layer-stack.js";

export interface UseLayerOptions {
  /** Register only while open. */
  open: boolean;
  /** Modal layers scroll-lock the body and inert the background while any is present. */
  modal?: boolean;
  kind?: "tooltip" | "reading" | undefined;
}

/* Modal side effects are counted so nested modal layers don't release early.

   Scroll lock: body overflow hidden + scrollbar padding compensation (no
   layout shift when the scrollbar disappears).

   Inert background: every body child except the layer root gets the `inert`
   attribute (strategy §13.4 — background invisible to AT and unfocusable
   while a modal layer is up). Only attributes WE set are removed on release. */
let modalCount = 0;
let savedOverflow = "";
let savedPaddingRight = "";
let inertedEls: HTMLElement[] = [];

const LAYER_ID_ATTR = "data-pbds-layer-id";
const MODAL_LAYER_ATTR = "data-pbds-modal-layer";
const MANAGED_INERT_ATTR = "data-pbds-managed-inert";

/**
 * Keep only the active modal layer interactive inside the shared portal root.
 * Body-level inert cannot do this by itself because every dialog portal is a
 * child of the same exempt root.
 */
function syncModalLayerIsolation(): void {
  const root = getLayerRoot();
  const activeModalId = topModalLayerId();
  for (const child of Array.from(root.children)) {
    if (!(child instanceof HTMLElement)) continue;
    const isInactiveModal =
      child.hasAttribute(MODAL_LAYER_ATTR) &&
      child.getAttribute(LAYER_ID_ATTR) !== activeModalId;
    if (isInactiveModal) {
      if (!child.hasAttribute("inert")) {
        child.setAttribute("inert", "");
        child.setAttribute(MANAGED_INERT_ATTR, "");
      }
      continue;
    }
    if (child.hasAttribute(MANAGED_INERT_ATTR)) {
      child.removeAttribute("inert");
      child.removeAttribute(MANAGED_INERT_ATTR);
    }
  }
}

function acquireModal(): void {
  if (modalCount === 0) {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;

    const root = getLayerRoot();
    for (const el of Array.from(document.body.children)) {
      if (el === root || !(el instanceof HTMLElement)) continue;
      if (el.tagName === "SCRIPT" || el.tagName === "STYLE") continue;
      if (el.hasAttribute("inert")) continue; // not ours to manage
      el.setAttribute("inert", "");
      inertedEls.push(el);
    }
  }
  modalCount += 1;
  syncModalLayerIsolation();
}

function releaseModal(): void {
  modalCount = Math.max(0, modalCount - 1);
  if (modalCount === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
    for (const el of inertedEls) el.removeAttribute("inert");
    inertedEls = [];
  }
  syncModalLayerIsolation();
}

/**
 * Registers an overlay in the layer stack while open (strategy §14.4).
 * Returns the layer id — pass it to useDismissable so Esc/outside-press
 * apply only when this layer is topmost.
 */
export function useLayer({
  open,
  modal = false,
  kind,
}: UseLayerOptions): string {
  const id = useId();

  useEffect(() => {
    if (!open) return;
    registerLayer({ id, modal, kind });
    if (modal) acquireModal();
    return () => {
      unregisterLayer(id);
      if (modal) releaseModal();
    };
  }, [open, modal, id, kind]);

  return id;
}
